import GameMenuPopup from '@/components/Popups/GameMenuPopup';
import RulesPopup from '@/components/Popups/RulesPopup/RulesPopup';
import ScoreBar from '@/components/ScoreBar/ScoreBar';
import { MenuIcon } from '@/assets/images';

import styles from './Game.module.css';
import type { TeamScores } from './types';

/**
 * Game HUD (head-up display) — the UI layer around the game table.
 *
 * Contains elements that live "above" the deck:
 * - rules popups
 * - the menu button
 * - the bottom score bar (including rotation based on the active team)
 *
 * Note: this is a pure UI component with no game domain logic.
 */
type Props = {
  scores: TeamScores;
  questionIndex: number;
  tableOrientationDeg: 0 | 180;
  isMenuOpen: boolean;
  isRulesOpen: boolean;
  translations: Record<string, string>;
  onOpenMenu: () => void;
  onCloseMenu: () => void;
  onOpenRules: () => void;
  onCloseRules: () => void;
};

const SCORE_BAR_COLORS = {
  trending: '#00A6E2',
  teletekst: '#CA51D8'
} as const;

const SCORE_BAR_STROKE_COLORS = {
  trending: '#0183B2',
  teletekst: '#A041AB'
} as const;

export const GameHUD = ({
  scores,
  questionIndex,
  tableOrientationDeg,
  isMenuOpen,
  isRulesOpen,
  translations,
  onOpenMenu,
  onCloseMenu,
  onOpenRules,
  onCloseRules
}: Props) => {
  const openRulesFromMenu = () => {
    onCloseMenu();
    onOpenRules();
  };

  return (
    <>
      {isMenuOpen && (
        <GameMenuPopup
          trendingPoints={scores.trending}
          teletekstPoints={scores.teletekst}
          translations={translations}
          onClose={onCloseMenu}
          onResume={onCloseMenu}
          onOpenRules={openRulesFromMenu}
        />
      )}

      {isRulesOpen && (
        <RulesPopup
          mode="inGame"
          translations={translations}
          onClose={onCloseRules}
          onPrimaryAction={onCloseRules}
        />
      )}

      <button
        type="button"
        className={styles.menuButton}
        aria-label={translations.menuLabel ?? 'Menu'}
        onClick={onOpenMenu}
      >
        <img src={MenuIcon} alt="" className={styles.menuIcon} />
      </button>

      <div className={styles.scoreContainer}>
        <div
          style={{
            transform: `rotate(${tableOrientationDeg}deg)`,
            transformOrigin: 'center'
          }}
        >
          <div
            className={
              tableOrientationDeg === 180
                ? `${styles.scoreBarAnimated} ${styles.rotated}`
                : styles.scoreBarAnimated
            }
          >
            <ScoreBar
              trending={scores.trending}
              teletekst={scores.teletekst}
              questionIndex={questionIndex}
              trendingColor={SCORE_BAR_COLORS.trending}
              teletekstColor={SCORE_BAR_COLORS.teletekst}
              trendingStrokeColor={SCORE_BAR_STROKE_COLORS.trending}
              teletekstStrokeColor={SCORE_BAR_STROKE_COLORS.teletekst}
            />
          </div>
        </div>
      </div>
    </>
  );
};

